import { createSlice } from '@reduxjs/toolkit';
import { enhancedCreditApi } from '../apis/endpoints/creditApi';

const initialState = {
  balance: null,
  periodStart: null,
  daysUntilReset: null,
  maxCredits: 3,
  isLoading: false,
  error: null,
  pendingOperations: 0
};

const creditSlice = createSlice({
  name: 'credit',
  initialState,
  reducers: {
    // Optimistic credit deduction (before API call)
    deductCreditOptimistic: (state, action) => {
      const amount = action?.payload?.amount || 1;
      if (state.balance >= amount) {
        state.balance -= amount;
        state.pendingOperations += 1;
      }
    },
    // Refund credit (on operation failure)
    refundCreditOptimistic: (state, action) => {
      const amount = action?.payload?.amount || 1;
      if (state.balance !== null) {
        state.balance += amount;
      }
      state.pendingOperations = Math.max(0, state.pendingOperations - 1);
    },
    // Confirm operation completed
    confirmOperation: (state) => {
      state.pendingOperations = Math.max(0, state.pendingOperations - 1);
    },
    // Set credit balance directly (from API response)
    setCreditBalance: (state, action) => {
      state.balance = action.payload.balance;
      state.periodStart = action.payload.periodStart;
      state.daysUntilReset = action.payload.daysUntilReset;
      if (action.payload.maxCredits !== undefined) {
        state.maxCredits = action.payload.maxCredits;
      }
      state.pendingOperations = 0;
    }
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        enhancedCreditApi.endpoints.getCreditBalance.matchFulfilled,
        (state, action) => {
          state.balance = action.payload.balance;
          state.periodStart = action.payload.periodStart;
          state.daysUntilReset = action.payload.daysUntilReset;
          state.maxCredits = action.payload.maxCredits;
          state.isLoading = false;
          state.error = null;
        }
      )
      .addMatcher(
        enhancedCreditApi.endpoints.getCreditBalance.matchPending,
        (state) => {
          state.isLoading = true;
        }
      )
      .addMatcher(
        enhancedCreditApi.endpoints.getCreditBalance.matchRejected,
        (state, action) => {
          state.isLoading = false;
          state.error = action.error?.message || 'Failed to fetch credits';
        }
      );
  }
});

export const {
  deductCreditOptimistic,
  refundCreditOptimistic,
  confirmOperation,
  setCreditBalance
} = creditSlice.actions;

export default creditSlice.reducer;
